<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import CmsPagesAPI from 'dashboard/api/cmsPages';
import Drawer from './Drawer.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Textarea from 'dashboard/components-next/textarea/TextArea.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const props = defineProps({
  page: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['close', 'save']);
const { t } = useI18n();

const loading = ref(false);
const activeTab = ref('basic');

const formData = ref({
  title: '',
  slug: '',
  description: '',
  meta_title: '',
  meta_description: '',
  meta_keywords: '',
  og_title: '',
  og_description: '',
  og_image: '',
  published: false,
  template: 'default',
});

const isEdit = computed(() => !!props.page);
const modalTitle = computed(() =>
  isEdit.value
    ? t('WEBSITE.CMS.FORM.EDIT_PAGE')
    : t('WEBSITE.CMS.FORM.NEW_PAGE')
);

const tabs = [
  {
    key: 'basic',
    label: t('WEBSITE.CMS.FORM.BASIC_INFO'),
    icon: 'i-lucide-file-text',
  },
  {
    key: 'seo',
    label: t('WEBSITE.CMS.FORM.SEO_INFO'),
    icon: 'i-lucide-search',
  },
];

onMounted(async () => {
  if (isEdit.value) {
    await loadPageDetails();
  }
});

const loadPageDetails = async () => {
  try {
    const response = await CmsPagesAPI.show(props.page.id);
    const page = response.data;

    formData.value = {
      title: page.title || '',
      slug: page.slug || '',
      description: page.description || '',
      meta_title: page.meta_title || '',
      meta_description: page.meta_description || '',
      meta_keywords: page.meta_keywords || '',
      og_title: page.og_title || '',
      og_description: page.og_description || '',
      og_image: page.og_image || '',
      published: page.published || false,
      template: page.template || 'default',
    };
  } catch (error) {
    useAlert(t('WEBSITE.CMS.API.ERROR.FETCH_FAILED'));
  }
};

// Auto-génération du slug depuis le titre
watch(
  () => formData.value.title,
  newTitle => {
    if (!isEdit.value && newTitle) {
      formData.value.slug = newTitle
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }
  }
);

const handleSubmit = async () => {
  if (!formData.value.title || !formData.value.slug) {
    useAlert(t('COMPONENTS.FORM.VALIDATION.REQUIRED_FIELDS'));
    return;
  }

  loading.value = true;

  try {
    const payload = {
      cms_page: formData.value,
    };

    if (isEdit.value) {
      await CmsPagesAPI.update(props.page.id, payload);
      useAlert(t('WEBSITE.CMS.API.SUCCESS.PAGE_UPDATED'));
    } else {
      await CmsPagesAPI.create(payload);
      useAlert(t('WEBSITE.CMS.API.SUCCESS.PAGE_CREATED'));
    }

    emit('save');
  } catch (error) {
    const message = isEdit.value
      ? t('WEBSITE.CMS.API.ERROR.UPDATE_FAILED')
      : t('WEBSITE.CMS.API.ERROR.CREATE_FAILED');
    useAlert(message);
  } finally {
    loading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};
</script>

<template>
  <Drawer :show="true" size="lg" @close="handleClose">
    <div class="flex flex-col h-full">
      <!-- Header -->
      <div class="flex items-center justify-between p-6 border-b border-n-weak">
        <h2 class="text-xl font-semibold text-n-slate-12">
          {{ modalTitle }}
        </h2>
        <button
          class="p-2 rounded-lg hover:bg-n-alpha-2 transition-colors"
          @click="handleClose"
        >
          <span class="i-lucide-x size-5 text-n-slate-11" />
        </button>
      </div>

      <!-- Tabs -->
      <div class="flex gap-4 px-6 pt-4 border-b border-n-weak">
        <button
          v-for="tab in tabs"
          :key="tab.key"
          class="flex items-center gap-2 px-4 py-2 text-sm font-medium transition-all border-b-2 -mb-px"
          :class="[
            activeTab === tab.key
              ? 'border-n-blue-9 text-n-blue-11'
              : 'border-transparent text-n-slate-11 hover:text-n-slate-12',
          ]"
          @click="activeTab = tab.key"
        >
          <span class="size-4" :class="[tab.icon]" />
          {{ tab.label }}
        </button>
      </div>

      <!-- Content -->
      <div class="flex-1 overflow-y-auto p-6">
        <!-- Basic Info Tab -->
        <div v-show="activeTab === 'basic'" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.CMS.FORM.TITLE') }} *
            </label>
            <Input
              v-model="formData.title"
              :placeholder="t('WEBSITE.CMS.FORM.TITLE_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.CMS.FORM.SLUG') }} *
            </label>
            <Input
              v-model="formData.slug"
              :placeholder="t('WEBSITE.CMS.FORM.SLUG_PLACEHOLDER')"
              class="w-full"
            />
            <p class="mt-1 text-xs text-n-slate-10">
              URL: /{{ formData.slug || 'page-url' }}
            </p>
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.CMS.FORM.DESCRIPTION') }}
            </label>
            <Textarea
              v-model="formData.description"
              :placeholder="t('WEBSITE.CMS.FORM.DESCRIPTION_PLACEHOLDER')"
              rows="3"
              class="w-full"
            />
          </div>

          <div>
            <label class="flex items-center gap-2">
              <Checkbox v-model="formData.published" />
              <span class="text-sm font-medium text-n-slate-12">
                {{ t('WEBSITE.CMS.FORM.PUBLISHED') }}
              </span>
            </label>
          </div>
        </div>

        <!-- SEO Tab -->
        <div v-show="activeTab === 'seo'" class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.CMS.FORM.META_TITLE') }}
            </label>
            <Input
              v-model="formData.meta_title"
              :placeholder="t('WEBSITE.CMS.FORM.META_TITLE_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.CMS.FORM.META_DESCRIPTION') }}
            </label>
            <Textarea
              v-model="formData.meta_description"
              :placeholder="t('WEBSITE.CMS.FORM.META_DESCRIPTION_PLACEHOLDER')"
              rows="3"
              class="w-full"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
              {{ t('WEBSITE.CMS.FORM.META_KEYWORDS') }}
            </label>
            <Input
              v-model="formData.meta_keywords"
              :placeholder="t('WEBSITE.CMS.FORM.META_KEYWORDS_PLACEHOLDER')"
              class="w-full"
            />
          </div>

          <div class="pt-4 border-t border-n-weak">
            <h3 class="text-sm font-semibold text-n-slate-12 mb-3">
              Open Graph
            </h3>

            <div class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.CMS.FORM.OG_TITLE') }}
                </label>
                <Input v-model="formData.og_title" class="w-full" />
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.CMS.FORM.OG_DESCRIPTION') }}
                </label>
                <Textarea
                  v-model="formData.og_description"
                  rows="2"
                  class="w-full"
                />
              </div>

              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  {{ t('WEBSITE.CMS.FORM.OG_IMAGE') }}
                </label>
                <Input
                  v-model="formData.og_image"
                  placeholder="https://..."
                  class="w-full"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        class="flex items-center justify-end gap-3 p-6 border-t border-n-weak"
      >
        <Button
          :label="t('WEBSITE.CMS.FORM.CANCEL')"
          color="slate"
          variant="outline"
          @click="handleClose"
        />
        <Button
          :label="t('WEBSITE.CMS.FORM.SAVE')"
          :loading="loading"
          @click="handleSubmit"
        />
      </div>
    </div>
  </Drawer>
</template>
